import { useTranslation } from 'react-i18next';
import { Box, IconButton, Chip, Grid, Typography } from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useMemo, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import {
	OutCentreFilter,
	applicationListItem,
} from '@/api/DTO/user/application.interface';
import FilterSection from './components/filterSection';
import { useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { selectMyInfo } from '@/pages/auth/authSlice';
import { useSelector } from 'react-redux';
import { useGetApplicationListQuery } from '@/api/user/applicationApiSlice';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import qs from 'qs';
import dayjs from 'dayjs';
import { DISPLAY_DATE_FORMAT } from '@/utils/dateHelper';
import DetailDialog from './components/detailDialog';
import { ApplyEvent } from '@/api/enum/common.enum';

export interface OutCentreList {
	id: number;
	name: string;
	applyAt: string;
	toCentre: string;
	serviceNumber: string;
}

export default function JoinCentrePage() {
	const { t } = useTranslation();
	const myInfo = useSelector(selectMyInfo);
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const [showDetailDialog, setShowDetailDialog] = useState(false);
	const [detailObj, setDetailObj] = useState({});

	const handleDetail = (obj: { transferId: any; status: any }) => {
		setShowDetailDialog(true);
		setDetailObj({
			transferId: obj.transferId,
			status: obj.status,
		});
	};

	const defaultPropertyFilter: OutCentreFilter = {
		pagination: initialPagination,
		loginID: '',
		sortings: [],
		event: ApplyEvent.outCentre,
		centreEnum: myInfo?.centre || '',
		statusList: [1],
	};
	const defaultValues: DefaultValues<OutCentreFilter> = defaultPropertyFilter;
	const methods = useForm<OutCentreFilter>({
		defaultValues,
	});

	const transformedSearchParams: OutCentreFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'loginID',
			'event',
			'centreEnum',
			'statusList',
		]),
	};

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as OutCentreFilter;
			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetApplicationListQuery(
			{
				...transformedSearchParams,
				statusList: transformedSearchParams.statusList
					? transformedSearchParams.statusList
					: [],
			},
			{
				refetchOnMountOrArgChange: true,
				skip: !myInfo?.centre,
			}
		);

	const transformResponse = useCallback((response?: applicationListItem[]) => {
		if (response == null) return;
		const newResponse: OutCentreList[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				toCentre: !item.toCentre ? '-' : item.toCentre,
				serviceNumber: !item.serviceNumber ? '-' : item.serviceNumber,
				applyAt: !item.applyAt
					? '-'
					: dayjs(item.applyAt)
							.locale('zh-hk-my-setting')
							.format(DISPLAY_DATE_FORMAT),
			});
		});
		return newResponse;
	}, []);

	const statusList = [
		{
			id: 1,
			name: t('common.willOutCentre'),
			chipColor: 'chipTertiary',
		},
		{
			id: 2,
			name: t('common.onOutCentre'),
			chipColor: 'chipGrey',
		},
	];

	const statusMap = useMemo(() => {
		const obj: Record<string, any> = {};
		statusList.forEach((item) => {
			obj[item.id] = item;
		});
		return obj;
	}, [statusList]);

	const columns: GridColDef[] = [
		{
			field: 'username',
			headerName: t('common.username'),
			flex: 27,
			renderCell: (params) => (
				<Box>
					<Typography
						sx={{
							display: 'block',
						}}
						fontSize='body2'
						variant='regular'
					>
						{params.row.username}
					</Typography>
					<Typography
						sx={{
							display: 'block',
						}}
						fontSize='caption'
						color='common.neutral50'
						variant='regular'
					>
						{params.row.phone}
					</Typography>
					<Typography
						sx={{
							display: 'block',
						}}
						fontSize='caption'
						color='common.neutral50'
						variant='regular'
					>
						{params.row.email}
					</Typography>
				</Box>
			),
		},
		{
			field: 'fromDistrict',
			headerName: t('application.district'),
			flex: 13,
		},
		{
			field: 'serviceNumber',
			headerName: t('common.historyServiceNumber'),
			flex: 13,
		},
		{
			field: 'toCentre',
			headerName: t('common.toCentre'),
			flex: 13,
		},
		{
			field: 'toDistrict',
			headerName: t('common.toDistrict'),
			flex: 13,
		},
		{
			field: 'applyAt',
			headerName: t('common.applyTime'),
			flex: 13,
		},
		{
			field: 'status',
			headerName: t('common.outCentreStatus'),
			flex: 13,
			renderCell: (params) => (
				<Chip
					label={statusMap[params.row.status].name}
					color={statusMap[params.row.status].chipColor}
					variant='outlined'
				/>
			),
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<div onClick={() => handleDetail(params.row)}>
					<IconButton color='primary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</div>
			),
		},
	];

	return (
		<>
			<FormProvider {...methods}>
				<FilterSection statusInput statusList={statusList}></FilterSection>

				<Box marginTop={2}>
					<AppDataGrid
						loading={!isGetPropertySummarySuccess}
						rows={transformResponse(propertySummary?.data?.items) ?? []}
						columns={columns}
						rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
						getRowId={(row) => row.transferId}
					/>
				</Box>
			</FormProvider>

			{showDetailDialog && (
				<DetailDialog
					isOpen={showDetailDialog}
					setIsOpen={setShowDetailDialog}
					type={ApplyEvent.outCentre}
					statusMap={statusMap}
					detailObj={detailObj}
				/>
			)}
		</>
	);
}
